import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  trackPageView, 
  trackScrollDepth, 
  trackSectionView, 
  trackTimeOnPage,
  trackExitIntent,
  trackPageAbandonment
} from '@/lib/analytics';

interface UsePageTrackingOptions {
  pageName: string;
  trackScroll?: boolean;
  trackTime?: boolean;
  trackExit?: boolean;
  scrollMilestones?: number[];
}

export function usePageTracking({
  pageName,
  trackScroll = true,
  trackTime = true,
  trackExit = true,
  scrollMilestones = [25, 50, 75, 90, 100],
}: UsePageTrackingOptions) {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const trackedMilestonesRef = useRef<Set<number>>(new Set());
  const maxScrollRef = useRef<number>(0);
  const lastInteractionRef = useRef<string>('page_load');

  // Track page view on mount
  useEffect(() => {
    trackPageView(location.pathname, pageName);
    startTimeRef.current = Date.now();
    trackedMilestonesRef.current = new Set();
    maxScrollRef.current = 0;
  }, [location.pathname, pageName]);

  // Track scroll depth
  useEffect(() => {
    if (!trackScroll) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      maxScrollRef.current = Math.max(maxScrollRef.current, scrollPercent);

      // Track milestones
      for (const milestone of scrollMilestones) {
        if (scrollPercent >= milestone && !trackedMilestonesRef.current.has(milestone)) {
          trackedMilestonesRef.current.add(milestone);
          trackScrollDepth(milestone, pageName);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackScroll, pageName, scrollMilestones]);

  // Track time on page
  useEffect(() => {
    if (!trackTime) return;

    // Track time at intervals
    const intervals = [30, 60, 120, 300]; // 30s, 1m, 2m, 5m
    const trackedIntervals = new Set<number>();

    const intervalId = setInterval(() => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      
      for (const threshold of intervals) {
        if (timeSpent >= threshold && !trackedIntervals.has(threshold)) {
          trackedIntervals.add(threshold);
          trackTimeOnPage(threshold, pageName);
        }
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [trackTime, pageName]);

  // Track exit intent (mouse leaving viewport)
  useEffect(() => {
    if (!trackExit) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        trackExitIntent(pageName, maxScrollRef.current);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [trackExit, pageName]);

  // Track abandonment on unmount
  useEffect(() => {
    return () => {
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeSpent > 5) { // Only track if spent more than 5 seconds
        trackPageAbandonment(pageName, timeSpent, lastInteractionRef.current);
      }
    };
  }, [pageName]);

  // Function to update last interaction
  const setLastInteraction = useCallback((interaction: string) => {
    lastInteractionRef.current = interaction;
  }, []);

  return { setLastInteraction, maxScrollRef };
}

// Hook for tracking section visibility with Intersection Observer
export function useSectionTracking(sectionName: string, pageName: string) {
  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedRef.current) {
            hasTrackedRef.current = true;
            trackSectionView(sectionName, pageName);
          }
        });
      },
      { threshold: 0.5 } // Track when 50% of section is visible
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [sectionName, pageName]);

  return sectionRef;
}

// HOC-style wrapper for section tracking
export function useMultipleSectionTracking(pageName: string) {
  const trackedSections = useRef<Set<string>>(new Set());

  const trackSection = useCallback((sectionName: string) => {
    if (!trackedSections.current.has(sectionName)) {
      trackedSections.current.add(sectionName);
      trackSectionView(sectionName, pageName);
    }
  }, [pageName]);

  const createSectionObserver = useCallback((sectionName: string, position?: number) => {
    return (element: HTMLElement | null) => {
      if (!element) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!trackedSections.current.has(sectionName)) {
                trackedSections.current.add(sectionName);
                trackSectionView(sectionName, pageName, position);
              }
              observer.disconnect();
            }
          });
        },
        { threshold: 0.3 }
      );

      observer.observe(element);
    };
  }, [pageName]);

  return { trackSection, createSectionObserver };
}
